import { memo } from 'react';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { NoteContent } from '@/components/NoteContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PostActions } from '@/desktop/components/PostActions';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * A single note rendered as a feed card: avatar, author, content (click to
 * open thread) and the reply/repost/like/zap action bar. Shared across the
 * Feed (Following / Global / Discover) and Search apps.
 *
 * Memoized: feed lists re-render on every refetch/sort change, but a card only
 * needs to re-render when its event (or callbacks) actually change.
 */
export const PostCard = memo(function PostCard({
  event,
  onOpenThread,
  onOpenProfile,
}: {
  event: NostrEvent;
  onOpenThread: (event: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
}) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);
  const isReply = event.tags.some(([t]) => t === 'e');

  return (
    // content-visibility lets the browser skip layout/paint for off-screen
    // cards — cheap virtualization that keeps long infinite feeds responsive
    // (and keeps off-screen images/videos from loading).
    <div className="px-3 sm:px-4 py-3 border-b border-border/40 hover:bg-muted/20 transition-colors [content-visibility:auto] [contain-intrinsic-size:auto_200px]">
      <div className="flex gap-2.5 sm:gap-3">
        <button
          className="shrink-0 mt-0.5"
          onClick={(e) => { e.stopPropagation(); onOpenProfile(event.pubkey); }}
        >
          <Avatar className="w-9 h-9">
            <AvatarImage src={metadata?.picture} alt={displayName} />
            <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 mb-0.5 flex-wrap">
            <button
              className="font-semibold text-sm hover:underline truncate max-w-[140px]"
              onClick={(e) => { e.stopPropagation(); onOpenProfile(event.pubkey); }}
            >
              {displayName}
            </button>
            {metadata?.nip05 && (
              <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">
                {metadata.nip05.replace(/^_@/, '')}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
              {formatRelativeTime(event.created_at)}
            </span>
          </div>
          {isReply && (
            <div className="text-[10px] text-muted-foreground mb-1">↩ replying</div>
          )}
          {/* Clickable content area opens thread */}
          <button
            className="w-full text-left"
            onClick={() => onOpenThread(event)}
          >
            <NoteContent
              event={event}
              className="text-sm leading-relaxed"
              disableNoteEmbeds={false}
              disableMediaEmbeds={false}
            />
          </button>
          {/* Action bar — stops propagation so clicks don't open thread */}
          <PostActions event={event} compact />
        </div>
      </div>
    </div>
  );
});
